import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList } from "recharts";
import SectionReveal from "./SectionReveal";
import SectionBanner from "./SectionBanner";
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { foreground, muted, border, body } from "./tokens";
import { FONT_BODY, FONT_DISPLAY, TEXT_PRIMARY, TEXT_SECONDARY } from "./BrandChart";

import type { ReportHostedSectionProps } from '../reportHostedSection.types';

/** Studio legend copy for each HARP status — static definitions, not warehouse fields. */
const HARP_STATUS_DESCRIPTIONS: Record<string, string> = {
  Undecided:
    "Member has been spoken to about HARP but has not agreed or refused.",
  "Enrolled in HARP":
    "Current or formerly In Program member has confirmed enrollment in HARP.",
  Refused: "Member refused to enroll in a HARP.",
  "In Progress": "In Program member has agreed but enrollment is not complete.",
};

const DEFAULT_ROWS = [
  {
    label: "Undecided",
    value: 47,
    color: "var(--reema-coral-100)",
    description: HARP_STATUS_DESCRIPTIONS.Undecided,
  },
  {
    label: "Enrolled in HARP",
    value: 18,
    color: "var(--reema-midnight-100)",
    description: HARP_STATUS_DESCRIPTIONS["Enrolled in HARP"],
  },
  {
    label: "Refused",
    value: 10,
    color: "#0A3356",
    description: HARP_STATUS_DESCRIPTIONS.Refused,
  },
  {
    label: "In Progress",
    value: 3,
    color: "#154A74",
    description: HARP_STATUS_DESCRIPTIONS["In Progress"],
  },
];

const PALETTE = [
  "var(--reema-coral-100)",
  "var(--reema-midnight-100)",
  "#0A3356",
  "#154A74",
];

const readRows = (data: Record<string, unknown>) => {
  if (!Array.isArray(data.statuses)) {
    return DEFAULT_ROWS;
  }
  return (
    data.statuses as Array<{ label: string; value: number }>
  ).map((row, index) => ({
    label: row.label,
    value: row.value,
    color: PALETTE[index] ?? PALETTE[PALETTE.length - 1],
    description: HARP_STATUS_DESCRIPTIONS[row.label] ?? "",
  }));
};

const HarpEnrollment = ({ data }: ReportHostedSectionProps) => {
  const rows = readRows(data);

  return (
    <HostedReportSectionLayout
    sectionDomId="harp"
    className="section--white report-hosted-section-body"
    style={{ background: "var(--white)", fontFamily: body }}
    banner={
      <SectionBanner title="HARP Enrollment" iconHref="#icon_relationship_engine" />
    }
  >

      <SectionReveal delay={0.1}>
        <div
          className="rounded-2xl p-6 md:p-8 mb-10"
          style={{ background: "#fff", border: `1px solid ${border}` }}
        >
          <div className="harp-enrollment-content-grid grid md:grid-cols-2 gap-8 items-center">
            <div className="harp-enrollment-chart" style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 24, right: 8, bottom: 24, left: 8 }}>
                  <YAxis type="number" hide />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 500, fill: TEXT_SECONDARY }}
                    interval={0}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={32}>
                    {rows.map((r) => (
                      <Cell key={r.label} fill={r.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      style={{
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 500,
                        fontSize: 14,
                        fill: TEXT_PRIMARY,
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="harp-enrollment-legend space-y-4">
              {rows.map((s) => (
                <div key={s.label}>
                  <p style={{ fontFamily: body, fontSize: 14, color: foreground, margin: 0 }}>
                    <strong style={{ fontWeight: 700 }}>{s.label}:</strong>{" "}
                    <span style={{ color: muted }}>{s.description}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default HarpEnrollment;
